import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import SkillsList from './SkillsList';
import { API_CONFIG, getApiUrl, apiFetch } from '../../config/api';
import ViewSkill from './ViewSkill';
import './Skills.css';

function Skills() {
  const { t } = useTranslation('admin');
  const [skills, setSkills] = useState([]);
  const [selectedSkill, setSelectedSkill] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [createMode, setCreateMode] = useState(false);
  const viewSkillRef = useRef();

  useEffect(() => {
    fetchSkills();
    setupToolbar();

    return () => {
      if (window.setSkillsToolbar) {
        window.setSkillsToolbar(null);
      }
    };
  }, []);

  useEffect(() => {
    updateToolbar();
  }, [selectedSkill, editMode, createMode]);

  const fetchSkills = async () => {
    setLoading(true);
    try {
      const response = await apiFetch(getApiUrl('/api/skills/?limit=1000'));
      if (response.ok) {
        const data = await response.json();
        setSkills(data.skills || []);
      }
    } catch (error) {
      console.error('Error fetching skills:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSkillSelect = (skill) => {
    setSelectedSkill(skill);
    setEditMode(false);
    setCreateMode(false);
  };

  const handleNew = () => {
    setSelectedSkill(null);
    setEditMode(false);
    setCreateMode(true);
  };

  const handleEdit = () => {
    if (selectedSkill) {
      setEditMode(true);
      setCreateMode(false);
    }
  };

  const handleSave = () => {
    if (viewSkillRef.current) {
      viewSkillRef.current.handleSave();
    }
  };

  const handleDelete = () => {
    if (viewSkillRef.current) {
      viewSkillRef.current.handleDelete();
    }
  };

  const handleCancel = () => {
    setEditMode(false);
    setCreateMode(false);
  };

  const handleSkillCreated = (newSkill) => {
    fetchSkills();
    setSelectedSkill(newSkill);
    setCreateMode(false);
  };

  const handleSaveComplete = () => {
    fetchSkills();
    setEditMode(false);
    if (selectedSkill) {
      // Refresh the selected skill
      setSelectedSkill(prev => ({ ...prev }));
    }
  };

  const handleBackToList = () => {
    setSelectedSkill(null);
    setEditMode(false);
    setCreateMode(false);
    fetchSkills();
  };

  const setupToolbar = () => {
    // Set up toolbar callback for parent component
    if (window.setSkillsToolbar) {
      updateToolbar();
    }
  };

  const updateToolbar = () => {
    if (!window.setSkillsToolbar) return;

    const buttons = [];

    if (createMode || editMode) {
      buttons.push(
        <button key="save" onClick={handleSave} className="toolbar-button primary">
          💾 {t('skills.toolbar.save')}
        </button>
      );
      buttons.push(
        <button key="cancel" onClick={handleCancel} className="toolbar-button">
          ✕ {t('skills.toolbar.cancel')}
        </button>
      );
    } else {
      buttons.push(
        <button key="new" onClick={handleNew} className="toolbar-button primary">
          ➕ {t('skills.toolbar.newSkill')}
        </button>
      );

      if (selectedSkill) {
        buttons.push(
          <button key="edit" onClick={handleEdit} className="toolbar-button">
            ✏️ {t('skills.toolbar.edit')}
          </button>
        );
        buttons.push(
          <button key="delete" onClick={handleDelete} className="toolbar-button danger">
            🗑️ {t('skills.toolbar.delete')}
          </button>
        );
      }

      buttons.push(
        <button key="refresh" onClick={fetchSkills} className="toolbar-button">
          🔄 {t('skills.toolbar.refresh')}
        </button>
      );
    }

    window.setSkillsToolbar(buttons);
  };

  return (
    <div className="skills-container">
      <div className="skills-master-detail">
        <div className="skills-list-panel">
          <SkillsList
            skills={skills}
            selectedSkill={selectedSkill}
            onSelectSkill={handleSkillSelect}
            loading={loading}
          />
        </div>
        <div className="skills-detail-panel">
          <ViewSkill
            ref={viewSkillRef}
            skillId={selectedSkill?.skill_id}
            onBack={handleBackToList}
            editMode={editMode}
            createMode={createMode}
            onSkillCreated={handleSkillCreated}
            onSave={handleSaveComplete}
            onCancel={handleCancel}
            onEdit={handleEdit}
          />
        </div>
      </div>
    </div>
  );
}

export default Skills;
